import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/contexts/LanguageContext";
import Layout from "./components/layout/Layout";

// Lazy load all pages for better performance
const Index = lazy(() => import("./pages/Index"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const CustomFurnitureMainPage = lazy(() => import("./pages/CustomFurnitureMainPage"));
const CustomFurniturePage = lazy(() => import("./pages/CustomFurniturePage"));
const BlindsCurtainsMainPage = lazy(() => import("./pages/BlindsCurtainsMainPage"));
const BlindsMainPage = lazy(() => import("./pages/BlindsMainPage"));
const CurtainsMainPage = lazy(() => import("./pages/CurtainsMainPage"));
const BlindsCurtainsPage = lazy(() => import("./pages/BlindsCurtainsPage"));
const UpholsteryMainPage = lazy(() => import("./pages/UpholsteryMainPage"));
const UpholsteryPage = lazy(() => import("./pages/UpholsteryPage"));
const UpholsteryServicePage = lazy(() => import("./pages/UpholsteryServicePage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminProjectsPage = lazy(() => import("./pages/AdminProjectsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading fallback component - minimal to prevent jarring flash
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-muted-foreground">Loading...</span>
    </div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LanguageProvider>
          <AuthProvider>
            <Toaster />
            <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route element={<Layout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/customize-furniture" element={<CustomFurnitureMainPage />} />
                  <Route path="/customize-furniture/:category" element={<CustomFurniturePage />} />
                  <Route path="/blinds-curtains" element={<BlindsCurtainsMainPage />} />
                  <Route path="/blinds" element={<BlindsMainPage />} />
                  <Route path="/curtains" element={<CurtainsMainPage />} />
                  <Route path="/blinds-curtains/:type/:subcategory" element={<BlindsCurtainsPage />} />
                  <Route path="/upholstery" element={<UpholsteryMainPage />} />
                  <Route path="/upholstery/:type" element={<UpholsteryPage />} />
                  <Route path="/upholstery/:category/:service" element={<UpholsteryServicePage />} />
                </Route>
                {/* Admin routes without layout */}
                <Route path="/admin/login" element={<AdminLoginPage />} />
                <Route path="/admin/projects" element={<AdminProjectsPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </BrowserRouter>
          </AuthProvider>
        </LanguageProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
